import {
    useEffect,
    useState,
} from 'react';

// FNP-918: module-scoped filter store (checklist §7; FNP-815).
//
// ⚠️ THERE IS NO FILTER DRAWER IN THIS MODULE, AND THAT IS DELIBERATE — see
// §7 and §3's rule that "no filter" and "filter was forgotten" must not look
// identical in a diff.
//
// `listProducts` selects exactly TWO fields: `id` and `name` (open
// services/queries.ts). Both are free text. A filter drawer offering a
// dropdown over free text would either duplicate the keyword search or present
// an empty select, so there is nothing here an operator could meaningfully
// pick from. Building one would be serving a hypothetical use case — the
// checklist's own "do not build for hypothetical use cases" rule.
//
// The store itself is kept, wired, and tested because it costs nothing and it
// is the seam a real filter attaches to the day the API returns a filterable
// field (`active`, a category, a livemode flag). `useProducts` already applies
// it, so adding a drawer later is a drawer, not a refactor. Until then
// `activeFilterCount` is always 0 and no badge renders.
//
// ⚠️ Filters here would be applied CLIENT-SIDE regardless: `listProducts` takes
// no filter argument at all, unlike its invoices and prices siblings.
export type ProductsFilters = Record<string, string>;

let globalFilters: ProductsFilters = {};
const filterListeners: Set<(filters: ProductsFilters) => void> = new Set();

const notifyFilterChange = (filters: ProductsFilters) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

/** Test seam: resets the store so filters do not leak between test cases. */
export const resetProductsFilter = () => notifyFilterChange({});

export const useProductsFilter = () => {
    const [filters, setFilters] = useState<ProductsFilters>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: ProductsFilters) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    // Empty-string values are treated as unset so a cleared field would not
    // keep inflating the count.
    const activeFilterCount = Object.values(filters)
                                    .filter(value => value !== '' && value !== undefined)
                                    .length;

    return {
        filters,
        activeFilterCount,
        handleSetFilters: notifyFilterChange,
        handleClearFilters: () => notifyFilterChange({}),
    };
};
